/**
 * TranscriptViewer Component
 * 
 * Displays completed telehealth call transcripts with SOAP note generation.
 * Used in the EMR for reviewing past telehealth visits.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  Sparkles,
  Clock,
  User,
  Stethoscope,
  Download,
  Copy,
  CheckCircle2,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Calendar,
  Video,
  Edit3,
  Save,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';

interface TranscriptSegment {
  speaker: string;
  speaker_id?: string;
  text: string;
  timestamp?: string;
  is_final?: boolean;
}

interface Transcript {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string;
  appointment_id?: string;
  transcript_text: string;
  transcript_json?: TranscriptSegment[];
  ai_summary?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  soap_note_generated: boolean;
  generated_note_id?: string;
  duration_seconds?: number;
  word_count?: number;
  confidence_score?: number;
  created_at: string;
  processed_at?: string;
  patient?: {
    first_name: string;
    last_name: string;
  };
  practitioner?: {
    first_name: string;
    last_name: string;
  };
  appointment?: {
    start_time: string;
    status: string;
  };
}

interface GeneratedSOAP {
  id: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  is_ai_generated: boolean;
  created_at: string;
}

interface TranscriptViewerProps {
  transcript: Transcript;
  clinicId: string;
  onNoteGenerated?: (noteId: string) => void;
  onClose?: () => void;
  onTranscriptUpdated?: (transcript: Transcript) => void;
}

const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  transcript: initialTranscript,
  clinicId,
  onNoteGenerated,
  onClose,
  onTranscriptUpdated
}) => {
  const { toast } = useToast();
  const [transcript, setTranscript] = useState<Transcript>(initialTranscript);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedNote, setGeneratedNote] = useState<GeneratedSOAP | null>(null);
  const [showFullTranscript, setShowFullTranscript] = useState(true);
  const [showSummary, setShowSummary] = useState(true);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedSegments, setEditedSegments] = useState<TranscriptSegment[]>([]);

  // Enter edit mode
  const handleEnterEditMode = () => {
    if (transcript.transcript_json && Array.isArray(transcript.transcript_json)) {
      setEditedSegments([...transcript.transcript_json]);
    } else {
      // Convert plain text to segments if no JSON exists
      const lines = transcript.transcript_text.split('\n').filter(line => line.trim());
      const segments: TranscriptSegment[] = lines.map((line, index) => {
        const match = line.match(/^(Provider|Patient|Speaker):\s*(.*)$/i);
        if (match) {
          return { speaker: match[1], text: match[2], is_final: true };
        }
        return { speaker: 'Speaker', text: line, is_final: true };
      });
      setEditedSegments(segments);
    }
    setIsEditMode(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedSegments([]);
  };

  // Update a segment's speaker
  const handleSpeakerChange = (index: number, newSpeaker: string) => {
    const updated = [...editedSegments];
    updated[index] = { ...updated[index], speaker: newSpeaker };
    setEditedSegments(updated);
  };

  // Update a segment's text
  const handleTextChange = (index: number, newText: string) => {
    const updated = [...editedSegments];
    updated[index] = { ...updated[index], text: newText };
    setEditedSegments(updated);
  };

  // Save edited transcript
  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const response = await api.patch(
        `/emr/${clinicId}/transcripts/${transcript.id}`,
        {
          transcript_json: editedSegments
        }
      );

      if (response.data.success) {
        // Update local transcript state
        const updatedTranscript = {
          ...transcript,
          transcript_json: editedSegments,
          transcript_text: response.data.transcript.transcript_text,
          word_count: response.data.transcript.word_count
        };
        setTranscript(updatedTranscript);
        onTranscriptUpdated?.(updatedTranscript);
        
        setIsEditMode(false);
        setEditedSegments([]);
        
        toast({
          title: 'Transcript Updated',
          description: 'Speaker labels and text have been saved'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Save Failed',
        description: error.response?.data?.error || error.message,
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Generate SOAP note from transcript
  const handleGenerateSOAP = async () => {
    setIsGenerating(true);
    try {
      const response = await api.post(
        `/emr/${clinicId}/transcripts/${transcript.id}/generate-soap`
      );

      if (response.data.success) {
        setGeneratedNote(response.data.note);
        toast({
          title: 'SOAP Note Generated',
          description: 'AI has created a draft SOAP note from the transcript'
        });
        onNoteGenerated?.(response.data.note.id);
      }
    } catch (error: any) {
      toast({
        title: 'Generation Failed',
        description: error.response?.data?.message || error.message,
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy text to clipboard
  const handleCopy = async (text: string, section: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
    toast({
      title: 'Copied',
      description: `${section} copied to clipboard`
    });
  };

  // Download transcript as text file
  const handleDownload = () => {
    let content = `Telehealth Visit Transcript\n`;
    content += `Date: ${format(new Date(transcript.created_at), 'PPpp')}\n`;
    content += `Patient: ${transcript.patient?.first_name} ${transcript.patient?.last_name}\n`;
    content += `Provider: ${transcript.practitioner?.first_name} ${transcript.practitioner?.last_name}\n`;
    content += `Duration: ${formatDuration(transcript.duration_seconds || 0)}\n`;
    content += `\n${'='.repeat(50)}\n\n`;
    content += transcript.transcript_text;

    if (transcript.ai_summary) {
      content += `\n\n${'='.repeat(50)}\nAI Summary:\n${transcript.ai_summary}`;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${transcript.id.slice(0, 8)}-${format(new Date(transcript.created_at), 'yyyy-MM-dd')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get speaker style
  const getSpeakerStyle = (speaker: string) => {
    const isProvider = speaker.toLowerCase().includes('provider') ||
                       speaker.toLowerCase().includes('doctor');
    return {
      icon: isProvider ? Stethoscope : User,
      color: isProvider ? 'text-brand-teal' : 'text-brand-gold',
      bg: isProvider ? 'bg-brand-teal/10' : 'bg-brand-gold/10'
    };
  };

  // Render status badge
  const renderStatus = () => {
    switch (transcript.status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Ready
          </Badge>
        );
      case 'processing':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Processing
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <AlertCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-teal/20 flex items-center justify-center">
            <Video className="h-5 w-5 text-brand-teal" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">
              Telehealth Transcript
            </h2>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Calendar className="h-3 w-3" />
              {format(new Date(transcript.created_at), 'PPP')}
              <span className="text-slate-600">•</span>
              <Clock className="h-3 w-3" />
              {formatDuration(transcript.duration_seconds || 0)}
              {transcript.word_count && (
                <>
                  <span className="text-slate-600">•</span>
                  {transcript.word_count} words
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {renderStatus()}
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownload}
            className="border-slate-600 text-slate-300"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-4xl mx-auto">
          {/* AI Summary */}
          {transcript.ai_summary && (
            <Card className="bg-slate-800 border-brand-teal/20">
              <CardHeader className="pb-2">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setShowSummary(!showSummary)}
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-brand-teal" />
                    <CardTitle className="text-sm text-white">AI Summary</CardTitle>
                  </div>
                  {showSummary ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </CardHeader>
              {showSummary && (
                <CardContent className="pt-0">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    {transcript.ai_summary}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopy(transcript.ai_summary!, 'Summary')}
                    className="mt-2 text-slate-400 hover:text-white"
                  >
                    {copiedSection === 'Summary' ? (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    ) : (
                      <Copy className="h-3 w-3 mr-1" />
                    )}
                    Copy
                  </Button>
                </CardContent>
              )}
            </Card>
          )}

          {/* Generate SOAP Button */}
          {transcript.status === 'completed' && !transcript.soap_note_generated && !generatedNote && (
            <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-blue-400" />
                      Generate SOAP Note
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      Use AI to create a clinical SOAP note from this transcript
                    </p>
                  </div>
                  <Button
                    onClick={handleGenerateSOAP}
                    disabled={isGenerating}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Generate SOAP
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SOAP Note Already Exists */}
          {(transcript.soap_note_generated || generatedNote) && (
            <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                    <div>
                      <h3 className="text-white font-medium">SOAP Note Generated</h3>
                      <p className="text-sm text-slate-400">
                        AI clinical documentation has been created
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    onClick={() => {
                      // Navigate to the note
                      const noteId = generatedNote?.id || transcript.generated_note_id;
                      if (noteId) {
                        window.location.href = `/clinic/emr/notes/${noteId}`;
                      }
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Note
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generated SOAP Preview */}
          {generatedNote && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <FileText className="h-4 w-4 text-brand-teal" />
                  Generated SOAP Note (Draft)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: 'Subjective', content: generatedNote.subjective },
                  { label: 'Objective', content: generatedNote.objective },
                  { label: 'Assessment', content: generatedNote.assessment },
                  { label: 'Plan', content: generatedNote.plan }
                ].map(section => (
                  <div key={section.label}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-brand-teal uppercase">
                        {section.label}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopy(section.content, section.label)}
                        className="h-6 text-slate-400 hover:text-white"
                      >
                        {copiedSection === section.label ? (
                          <CheckCircle2 className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    <p className="text-sm text-slate-300">{section.content}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Full Transcript */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => !isEditMode && setShowFullTranscript(!showFullTranscript)}
              >
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  Full Transcript
                  {isEditMode && (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 ml-2">
                      <Edit3 className="h-3 w-3 mr-1" />
                      Editing
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {isEditMode ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelEdit();
                        }}
                        className="text-slate-400 hover:text-white"
                        disabled={isSaving}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveEdit();
                        }}
                        className="bg-brand-teal hover:bg-brand-teal/80 text-white"
                        disabled={isSaving}
                      >
                        {isSaving ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-3 w-3 mr-1" />
                        )}
                        Save Changes
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEnterEditMode();
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopy(transcript.transcript_text, 'Transcript');
                        }}
                        className="text-slate-400 hover:text-white"
                      >
                        {copiedSection === 'Transcript' ? (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        ) : (
                          <Copy className="h-3 w-3 mr-1" />
                        )}
                        Copy
                      </Button>
                      {showFullTranscript ? (
                        <ChevronUp className="h-4 w-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                      )}
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            {(showFullTranscript || isEditMode) && (
              <CardContent>
                {isEditMode ? (
                  // Edit Mode: Editable segments
                  <div className="space-y-4">
                    <p className="text-xs text-slate-500 mb-4">
                      Click on speaker labels to change them. Edit text directly in the text fields.
                    </p>
                    {editedSegments.map((segment, index) => (
                      <div key={index} className="flex gap-3 items-start p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                        <div className="w-28 flex-shrink-0">
                          <Select
                            value={segment.speaker}
                            onValueChange={(value) => handleSpeakerChange(index, value)}
                          >
                            <SelectTrigger className="h-8 text-xs bg-slate-800 border-slate-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              <SelectItem value="Provider" className="text-white hover:bg-slate-700">
                                <div className="flex items-center gap-2">
                                  <Stethoscope className="h-3 w-3 text-brand-teal" />
                                  Provider
                                </div>
                              </SelectItem>
                              <SelectItem value="Patient" className="text-white hover:bg-slate-700">
                                <div className="flex items-center gap-2">
                                  <User className="h-3 w-3 text-brand-gold" />
                                  Patient
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Textarea
                          value={segment.text}
                          onChange={(e) => handleTextChange(index, e.target.value)}
                          className="flex-1 min-h-[60px] text-sm bg-slate-800 border-slate-600 text-slate-200 resize-none"
                          placeholder="Transcript text..."
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // View Mode: Display segments
                  transcript.transcript_json && Array.isArray(transcript.transcript_json) ? (
                    <div className="space-y-3">
                      {transcript.transcript_json.map((segment, index) => {
                        const style = getSpeakerStyle(segment.speaker);
                        const Icon = style.icon;
                        const prevSpeaker = index > 0 ? transcript.transcript_json![index - 1].speaker : null;
                        const showSpeaker = segment.speaker !== prevSpeaker;

                        return (
                          <div key={index}>
                            {showSpeaker && (
                              <div className="flex items-center gap-2 mb-1 mt-3">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${style.bg}`}>
                                  <Icon className={`h-3 w-3 ${style.color}`} />
                                </div>
                                <span className={`text-xs font-medium ${style.color}`}>
                                  {segment.speaker}
                                </span>
                              </div>
                            )}
                            <p className="text-sm text-slate-300 pl-7">{segment.text}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">
                      {transcript.transcript_text}
                    </p>
                  )
                )}
              </CardContent>
            )}
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
};

export default TranscriptViewer;
